import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  FiSearch, 
  FiCompass, 
  FiEdit, 
  FiCode, 
  FiCheckSquare, 
  FiTrendingUp, 
  FiLifeBuoy 
} from "react-icons/fi";

const DevelopmentProcess = () => {
  const { t } = useTranslation();

  const steps = [
    {
      icon: FiSearch,
      num: "01",
      titleKey: "proc_disc_title",
      defaultTitle: "Discovery",
      descKey: "proc_disc_desc",
      defaultDesc: "We sit down with you to explore requirements, refine targets, analyze target markets, and evaluate feasibility.",
    },
    {
      icon: FiCompass,
      num: "02",
      titleKey: "proc_plan_title",
      defaultTitle: "Planning",
      descKey: "proc_plan_desc",
      defaultDesc: "Creating comprehensive architecture maps, timelines, choosing technology stacks, and structuring the sprints.",
    },
    {
      icon: FiEdit,
      num: "03",
      titleKey: "proc_design_title",
      defaultTitle: "UI/UX Design",
      descKey: "proc_design_desc",
      defaultDesc: "Developing wireframes, user journeys, responsive high-fidelity interactive mockups, and cohesive style guides.",
    },
    {
      icon: FiCode,
      num: "04",
      titleKey: "proc_dev_title",
      defaultTitle: "Development",
      descKey: "proc_dev_desc",
      defaultDesc: "Clean modular programming using version control, writing API endpoints, and building pixel-perfect frontends.",
    },
    {
      icon: FiCheckSquare,
      num: "05",
      titleKey: "proc_test_title",
      defaultTitle: "Testing",
      descKey: "proc_test_desc",
      defaultDesc: "Deploying automated QA suites, cross-browser compatibility tests, load testing, and manual user reviews.",
    },
    {
      icon: FiTrendingUp,
      num: "06",
      titleKey: "proc_deploy_title",
      defaultTitle: "Deployment",
      descKey: "proc_deploy_desc",
      defaultDesc: "Setting up secure cloud operations (AWS/Docker/Firebase), releasing to stores, and configuring analytics tracking.",
    },
    {
      icon: FiLifeBuoy,
      num: "07",
      titleKey: "proc_support_title",
      defaultTitle: "Support",
      descKey: "proc_support_desc",
      defaultDesc: "Adopting ongoing updates, monitoring performance bottlenecks, and providing long-term support SLAs.",
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-slate-50 dark:bg-[#0c0c0e] transition-colors duration-500 overflow-hidden relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        
        {/* Section Title */}
        <div className="text-center mb-20">
          <span className="text-xs font-bold text-blue-600 dark:text-purple-400 uppercase tracking-widest">
            {t('processLabel', 'Work Flow')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mt-2 tracking-tight">
            {t('processTitle', 'Our Development Process')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-purple-500 dark:to-pink-500 mx-auto mt-4 rounded-full" />
        </div>

        {/* Timeline Container */}
        <div className="relative max-w-5xl mx-auto">
          {/* Vertical central line for desktop */}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-[2px] bg-slate-200 dark:bg-neutral-800 -translate-x-1/2" />
          
          {/* Scroll progress path overlay */}
          <motion.div 
            initial={{ height: 0 }}
            whileInView={{ height: "100%" }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, ease: "easeInOut" }}
            className="absolute left-4 md:left-1/2 top-0 w-[2px] bg-gradient-to-b from-blue-600 to-purple-600 dark:from-purple-500 dark:to-pink-500 -translate-x-1/2 origin-top"
          />

          <div className="flex flex-col gap-12 md:gap-16">
            {steps.map((step, index) => {
              const isEven = index % 2 === 0;
              return (
                <div 
                  key={index} 
                  className={`flex flex-col md:flex-row relative items-start md:items-center ${
                    isEven ? "md:flex-row-reverse" : ""
                  }`}
                >
                  {/* Timeline point indicator */}
                  <motion.div 
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true, amount: 0.8 }}
                    transition={{ type: "spring", stiffness: 150 }}
                    className="absolute left-4 md:left-1/2 w-8 h-8 rounded-full border-4 border-slate-50 dark:border-[#0c0c0e] bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-purple-500 dark:to-pink-500 shadow-md -translate-x-1/2 z-10 flex items-center justify-center text-[10px] text-white font-bold"
                  >
                    {step.num}
                  </motion.div>

                  {/* Left Column Spacer for desktop */}
                  <div className="hidden md:block w-1/2" />

                  {/* Card content column */}
                  <motion.div
                    initial={{ opacity: 0, x: isEven ? -50 : 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.6, type: "spring", stiffness: 80 }}
                    whileHover={{ scale: 1.01 }}
                    className="w-full md:w-[45%] pl-12 md:pl-0 flex flex-col"
                  >
                    <div className="p-6 sm:p-8 rounded-3xl bg-white dark:bg-[#111111] border border-slate-200/50 dark:border-neutral-800/40 shadow-sm dark:shadow-2xl hover:shadow-lg dark:hover:shadow-purple-500/5 hover:border-blue-500/20 dark:hover:border-purple-500/20 transition-all duration-300 relative group overflow-hidden">
                      {/* Glow blur background */}
                      <div className="absolute top-0 right-0 w-16 h-16 bg-blue-500/5 dark:bg-purple-500/5 rounded-full blur-lg" />
                      
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 bg-blue-50 dark:bg-neutral-900 text-blue-600 dark:text-purple-400 rounded-2xl group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-indigo-600 dark:group-hover:from-purple-500 dark:group-hover:to-pink-500 group-hover:text-white transition-all duration-300">
                          <step.icon className="w-5 h-5" />
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-extrabold text-blue-600 dark:text-purple-400 tracking-wider">
                            Step {step.num}
                          </span>
                          <h3 className="text-lg font-extrabold text-slate-900 dark:text-white mt-0.5">
                            {t(step.titleKey, step.defaultTitle)}
                          </h3>
                        </div>
                      </div>
                      
                      <p className="text-sm text-slate-500 dark:text-neutral-400 leading-relaxed">
                        {t(step.descKey, step.defaultDesc)}
                      </p>
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DevelopmentProcess;
